import { useLanguage } from '../context/LanguageContext';

/**
 * Hook to get localized content based on current language
 * Returns a function that takes TR content and EN content, returns the appropriate one
 */
export const useLocalized = () => {
  const { language } = useLanguage();

  /**
   * Get localized value - returns EN version if language is EN and EN version exists
   * @param tr Turkish value (required)
   * @param en English value (optional)
   * @returns The appropriate localized value
   */
  const getLocalized = <T>(tr: T, en?: T): T => {
    if (language === 'EN' && en !== undefined && en !== null && en !== '') {
      return en;
    }
    return tr;
  };

  /**
   * Get localized product name
   */
  const getProductName = (product: { name: string; name_en?: string }) => {
    return getLocalized(product.name, product.name_en);
  };

  /**
   * Get localized product description
   */
  const getProductDescription = (product: { description: string; description_en?: string }) => {
    return getLocalized(product.description, product.description_en);
  };

  /**
   * Get localized product short description
   */
  const getProductShortDescription = (product: { shortDescription?: string; shortDescription_en?: string }) => {
    return getLocalized(product.shortDescription || '', product.shortDescription_en);
  };

  /**
   * Get localized category name
   */
  const getCategoryName = (category: { name: string; name_en?: string }) => {
    return getLocalized(category.name, category.name_en);
  };

  /**
   * Get localized category description
   */
  const getCategoryDescription = (category: { description: string; description_en?: string }) => {
    return getLocalized(category.description, category.description_en);
  };

  /**
   * Get localized feature
   */
  const getFeature = (feature: { name: string; name_en?: string; value: string; value_en?: string }) => {
    return {
      name: getLocalized(feature.name, feature.name_en),
      value: getLocalized(feature.value, feature.value_en)
    };
  };

  /**
   * Get localized care instructions
   */
  const getCareInstructions = (product: { careInstructions?: string[]; careInstructions_en?: string[] }) => {
    return getLocalized(product.careInstructions || [], product.careInstructions_en);
  };

  /**
   * Get localized accessory name
   */
  const getAccessoryName = (accessory: { name: string; name_en?: string }) => {
    return getLocalized(accessory.name, accessory.name_en);
  };

  /**
   * Get localized accessory description
   */
  const getAccessoryDescription = (accessory: { description?: string; description_en?: string }) => {
    return getLocalized(accessory.description || '', accessory.description_en);
  };

  /**
   * Get localized API error message
   * API returns { message: 'Turkish', message_en: 'English' }
   */
  const getApiError = (error: any, fallbackTr = 'Bir hata oluştu', fallbackEn = 'An error occurred'): string => {
    const data = error?.response?.data;
    if (!data) {
      return getLocalized(fallbackTr, fallbackEn);
    }
    // Check for validation errors array
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      const firstError = data.errors[0];
      return getLocalized(firstError.msg, firstError.msg_en) || firstError.msg;
    }
    // Check for bilingual message
    return getLocalized(data.message, data.message_en) || data.message || getLocalized(fallbackTr, fallbackEn);
  };

  return {
    language,
    isEnglish: language === 'EN',
    isTurkish: language === 'TR',
    getLocalized,
    getApiError,
    getProductName,
    getProductDescription,
    getProductShortDescription,
    getCategoryName,
    getCategoryDescription,
    getFeature,
    getCareInstructions,
    getAccessoryName,
    getAccessoryDescription
  };
};

export default useLocalized;
